import { useCallback, useEffect, useState } from "react";
import {
  clampArtikelColumnWidth,
  getDefaultArtikelColumns,
  normalizeArtikelColumnSelection,
  type ArtikelColumnKey,
} from "@/pages/artikel/artikelColumns";

const STORAGE_KEY = "eh_artikel_table_columns_v1";
const PAGE_SIZE_STORAGE_KEY = "eh_artikel_page_size_v1";

/** Erlaubte Seitengrößen (API-Limit der Artikelliste: 1000). */
export const ARTIKEL_PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000] as const;
export const ARTIKEL_DEFAULT_PAGE_SIZE = 50;

export function normalizeArtikelPageSize(value: unknown): number {
  const n = Number(value);
  if ((ARTIKEL_PAGE_SIZE_OPTIONS as readonly number[]).includes(n)) return n;
  return ARTIKEL_DEFAULT_PAGE_SIZE;
}

export function readArtikelPageSize(): number {
  try {
    return normalizeArtikelPageSize(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
  } catch {
    return ARTIKEL_DEFAULT_PAGE_SIZE;
  }
}

export function writeArtikelPageSize(size: number): void {
  try {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(normalizeArtikelPageSize(size)));
  } catch {
    // localStorage nicht verfügbar – die Größe gilt nur für die Sitzung.
  }
}

export function useArtikelPageSize() {
  const [pageSize, setPageSizeState] = useState(readArtikelPageSize);

  const setPageSize = useCallback((next: number) => {
    const normalized = normalizeArtikelPageSize(next);
    setPageSizeState(normalized);
    writeArtikelPageSize(normalized);
  }, []);

  return { pageSize, setPageSize };
}

export interface ArtikelColumnView {
  columns: ArtikelColumnKey[];
  widths: Record<string, number>;
}

export interface ArtikelColumnSettings {
  desktop: ArtikelColumnView | null;
  mobile: ArtikelColumnView | null;
}

function readStored(): ArtikelColumnSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { desktop: null, mobile: null };
    const parsed = JSON.parse(raw) as ArtikelColumnSettings;
    return {
      desktop: parsed.desktop
        ? {
            columns: normalizeArtikelColumnSelection(parsed.desktop.columns),
            widths: parsed.desktop.widths || {},
          }
        : null,
      mobile: parsed.mobile
        ? {
            columns: normalizeArtikelColumnSelection(parsed.mobile.columns),
            widths: parsed.mobile.widths || {},
          }
        : null,
    };
  } catch {
    return { desktop: null, mobile: null };
  }
}

function writeStored(settings: ArtikelColumnSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function useArtikelTableSettings() {
  const [settings, setSettings] = useState<ArtikelColumnSettings>(() => readStored());

  useEffect(() => {
    writeStored(settings);
  }, [settings]);

  const saveSettings = useCallback(async (next: ArtikelColumnSettings) => {
    setSettings(next);
  }, []);

  return { settings, saveSettings };
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = () => setMatches(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

export function getVisibleArtikelColumns(
  settings: ArtikelColumnSettings,
  isMobile: boolean,
): ArtikelColumnKey[] {
  const view = isMobile ? settings.mobile : settings.desktop;
  if (view?.columns?.length) {
    return normalizeArtikelColumnSelection(view.columns);
  }
  return getDefaultArtikelColumns();
}

export function getVisibleArtikelWidths(
  settings: ArtikelColumnSettings,
  isMobile: boolean,
): Record<string, number> {
  const view = isMobile ? settings.mobile : settings.desktop;
  const widths = view?.widths ?? {};
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(widths)) {
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) out[key] = clampArtikelColumnWidth(num);
  }
  return out;
}
